import { debounce } from 'lodash';
import type { RouteLocationRaw } from 'vue-router';
import { useRouter } from 'vue-router';
import { useI18n } from '@/composables/useI18n';
import introJs from 'intro.js';
import type { LinkOptions } from '@/composables/useUrl';
import helpers from '@/services/helpers';
import { $emitter } from '@/services/emitter';
import { useStore } from '@/store';

export interface TutorialTexts {
  title: string;
  content: string;
}

const TUTORIAL_INDEX = [
  'campaigns',
  'traffic-sources',
  'affiliate-networks',
  'offers',
  'landings',
] as const;

type GetLink = (group: string, optionsParam?: LinkOptions) => RouteLocationRaw;

export const useTutorialApi = (getLink: GetLink = () => '') => {
  const store = useStore();
  const { t } = useI18n();
  const router = useRouter();
  const texts: Record<string, TutorialTexts> = {
    campaigns: {
      title: t('Campaign Settings'),
      content: t('Click \'New\' to set up a new campaign'),
    },
    'traffic-sources': {
      title: t('Traffic Source Settings'),
      content: t('Click \'New\' to add a new traffic source element'),
    },
    'affiliate-networks': {
      title: t('Affiliate Network Settings'),
      content: t('Click \'New\' to add a new affiliate network element'),
    },
    offers: {
      title: t('Offer Settings'),
      content: t('Click \'New\' to add a new offer element'),
    },
    landings: {
      title: t('Landing Page Settings'),
      content: t('Click \'New\' to add a new landing page'),
    },
  };

  const start = (page: string) => {
    const afterNavigate = () => {
      $emitter.emit('reload-report');
      helpers.afterBrowserRedraw(() => {
        store.setTutorialQuickStartStep(page);
      });
    };

    const link = getLink(page, {
      isReport: false,
    });
    Promise.resolve(router.push(link)).finally(afterNavigate);
  };

  const reset = () => {
    store.setTutorialQuickStartStep(0);
  };

  const close = () => {
    introJs().exit();
    store.setTutorialQuickStartStep(0);
    store.setTutorialQuickStartHiddenClass(false);
  };

  const firstStep = (value: string) => {
    if (!TUTORIAL_INDEX.includes(value as typeof TUTORIAL_INDEX[number])) return;

    store.setTutorialQuickStartSection(value);
    const sectionTexts = texts[value];
    const intro = `<div class="introjs-tooltip__title">${sectionTexts.title}</div><div class="introjs-tooltip__content">${sectionTexts.content}</div>`;
    const options = {
      exitOnEsc: false,
      exitOnOverlayClick: false,
      showStepNumbers: false,
      hidePrev: true,
      hideNext: true,
      skipLabel: t('End tour'),
      buttonClass: 'introjs-button introjs-button--hide',
      showBullets: false,
    };

    const entry: HTMLElement | null = document.querySelector('.tutorial-first-step');
    introJs()
      .addSteps([{ element: entry, intro }])
      .setOptions(options)
      .start()
      .onexit(() => {
        store.setTutorialQuickStartStep(0);
      });
  };

  const modal = debounce(() => {
    introJs().exit();
    store.setTutorialQuickStartStep('modal');
  });

  const form = debounce(() => {
    if (!store.tutorial.quickStart || store.tutorial.quickStartStep !== 'modal') return;

    store.setTutorialQuickStartHiddenClass(true);
    const options = {
      exitOnEsc: false,
      exitOnOverlayClick: false,
      showStepNumbers: false,
      hidePrev: true,
      hideNext: true,
      skipLabel: t('End tour'),
      nextLabel: t('Next »'),
      prevLabel: t('« Prev'),
      doneLabel: t('End tour'),
      showBullets: false,
    };

    const introInstance = introJs();
    introInstance
      .setOptions(options)
      .goToStepNumber(2)
      .start()
      .onbeforechange(() => {
        let continueStep = true;
        $emitter.emit('intro-js-onbeforechange', {
          introInstance,
          callback: (data: boolean) => {
            continueStep = data;
          },
        });
        return continueStep;
      })
      .onexit(() => {
        store.setTutorialQuickStartStep(0);
        store.setTutorialQuickStartHiddenClass(false);
      });
  }, 100);

  return {
    start,
    reset,
    close,
    texts,
    firstStep,
    modal,
    form,
  };
};
